<script lang="ts">
  import { onMount } from "svelte";
  import { api, type InstallerObject } from "../lib/api";
  import { haptic } from "../lib/tg";

  let { onhome }: { onhome?: () => void } = $props();

  let items = $state<InstallerObject[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let busy = $state<string | null>(null);     // `${lead}:${step}` in-flight
  let uploading = $state<number | null>(null); // lead_amo_id with in-flight photo
  let toast = $state<string | null>(null);

  const STEPS: Array<{ key: "drive" | "measure" | "deliver" | "install"; label: string }> = [
    { key: "drive", label: "🚗 Выехал" },
    { key: "measure", label: "📐 Замер" },
    { key: "deliver", label: "📦 Доставка" },
    { key: "install", label: "🔥 Монтаж" },
  ];

  function showToast(m: string, ms = 2600) {
    toast = m;
    setTimeout(() => { if (toast === m) toast = null; }, ms);
  }

  async function load() {
    loading = true;
    try {
      const r = await api.installerObjects();
      items = r.items;
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  }

  async function doStep(obj: InstallerObject, step: "drive" | "measure" | "deliver" | "install") {
    haptic("medium");
    busy = `${obj.lead_amo_id}:${step}`;
    try {
      const r = await api.installerStep(obj.lead_amo_id, step);
      // optimistic: mark step done in place
      items = items.map((o) =>
        o.lead_amo_id === obj.lead_amo_id ? { ...o, steps_done: { ...o.steps_done, [step]: true } } : o,
      );
      showToast(`✓ ${r.label}`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "ошибка");
    } finally {
      busy = null;
    }
  }

  // Field phones shoot 4–12MB JPEGs; downscale before upload so it survives poor
  // signal and the server cap. Falls back to the original on any failure (e.g. the
  // browser can't decode HEIC).
  async function compressImage(file: File): Promise<File> {
    if (!file.type.startsWith("image/")) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const maxDim = 1600;
      const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close?.();
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.82),
      );
      if (blob && blob.size > 0 && blob.size < file.size) {
        return new File([blob], "object.jpg", { type: "image/jpeg" });
      }
      return file;
    } catch {
      return file;
    }
  }

  async function onPhoto(obj: InstallerObject, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    haptic("medium");
    uploading = obj.lead_amo_id;
    try {
      const upload = await compressImage(file);
      const r = await api.installerPhoto(obj.lead_amo_id, upload);
      showToast(r.attached ? "✓ Фото в карточке amoCRM" : "✓ Фото отправлено");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "ошибка загрузки");
    } finally {
      uploading = null;
      input.value = "";
    }
  }

  function openAmo(url: string) {
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(url);
    else window.open(url, "_blank", "noopener");
  }

  onMount(load);
</script>

<div class="screen">
  <header class="hdr">
    <div class="title">🔧 Мои объекты {items.length ? `· ${items.length}` : ""}</div>
    <button class="refresh" onclick={load} aria-label="Обновить">⟳</button>
  </header>

  {#if loading}
    <div class="msg">Загружаю…</div>
  {:else if error}
    <div class="msg err">{error}</div>
  {:else if items.length === 0}
    <div class="msg">Нет объектов в работе.<br/>Сделки появятся здесь на стадиях <b>Доставка</b> и <b>Реализация</b>, либо когда директор отправит сделку «На монтаж».</div>
  {:else}
    <section class="list">
      {#each items as o (o.lead_amo_id)}
        <div class="card">
          <div class="card-top">
            <div class="lead">{o.lead_name}</div>
            {#if o.manual}<span class="badge manual">вручную</span>{/if}
          </div>
          {#if o.stage_name}<div class="stage">{o.stage_name}</div>{/if}

          {#if o.contacts.length}
            <div class="contacts">
              {#each o.contacts as c}
                <div class="contact">
                  <span class="cname">{c.name}</span>
                  {#if c.phone}<a class="ctel" href={`tel:${c.phone}`}>{c.phone}</a>{/if}
                </div>
              {/each}
            </div>
          {/if}

          {#if Object.keys(o.tech).length}
            <div class="tech">
              {#each Object.entries(o.tech) as [k, v]}
                <div class="tech-row"><span class="tk">{k}</span><span class="tv">{v}</span></div>
              {/each}
            </div>
          {/if}

          <div class="steps">
            {#each STEPS as s}
              <button class="step" class:done={o.steps_done[s.key]}
                      disabled={busy === `${o.lead_amo_id}:${s.key}` || o.steps_done[s.key]}
                      onclick={() => doStep(o, s.key)}>
                {#if busy === `${o.lead_amo_id}:${s.key}`}…{:else}{o.steps_done[s.key] ? "✓ " : ""}{s.label}{/if}
              </button>
            {/each}
          </div>

          <div class="actions">
            <label class="photo-btn" class:busy={uploading === o.lead_amo_id}>
              {uploading === o.lead_amo_id ? "Загружаю…" : "📷 Фото с объекта"}
              <input type="file" accept="image/*" capture="environment"
                     disabled={uploading === o.lead_amo_id}
                     onchange={(e) => onPhoto(o, e)} />
            </label>
            <button class="amo" onclick={() => openAmo(o.amo_url)}>🔗 amoCRM</button>
          </div>
        </div>
      {/each}
    </section>
  {/if}

  {#if toast}<div class="toast">{toast}</div>{/if}
</div>

<style>
  .screen { padding-bottom: 90px; min-height: 100vh; background: var(--bg); }
  .hdr {
    padding: calc(14px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid var(--border);
    position: sticky; top: 0; background: var(--bg); z-index: 5;
  }
  .title { font-family: 'Manrope', sans-serif; font-weight: 800; font-size: 17px; }
  .refresh {
    width: 36px; height: 36px; border-radius: 10px;
    border: 1px solid var(--border); background: var(--bg-elev);
    color: var(--text-mute); font-size: 18px; cursor: pointer;
  }
  .refresh:active { transform: scale(0.94); }
  .msg { padding: 48px 24px; text-align: center; color: var(--text-mute); font-size: 14px; line-height: 1.6; }
  .msg.err { color: var(--danger); }

  .list { padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; }
  .card {
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-left: 4px solid var(--ember);
    border-radius: 14px;
    padding: 14px;
    box-shadow: var(--shadow-sm);
  }
  .card-top { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
  .lead { font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 15px; }
  .badge.manual {
    flex-shrink: 0; font-size: 10px; font-weight: 700;
    background: var(--ember); color: #fff; border-radius: 7px; padding: 2px 7px;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .stage { margin-top: 3px; font-size: 12px; color: var(--brass); font-weight: 600; }

  .contacts { margin-top: 10px; display: flex; flex-direction: column; gap: 4px; }
  .contact { display: flex; gap: 10px; align-items: baseline; font-size: 13px; }
  .cname { color: var(--text-mute); }
  .ctel { color: var(--ember); font-weight: 600; text-decoration: none; }

  .tech { margin-top: 10px; display: flex; flex-direction: column; gap: 3px; padding: 8px 10px; background: var(--bg); border-radius: 10px; }
  .tech-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; }
  .tk { color: var(--text-dim); }
  .tv { color: var(--text); font-weight: 500; text-align: right; }

  .steps { margin-top: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .step {
    background: var(--bg-elev-2, var(--bg));
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 11px; padding: 11px 8px;
    color: var(--text); font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: 'Manrope', sans-serif;
  }
  .step:active { transform: scale(0.97); }
  .step.done { background: rgba(43,182,115,0.14); border-color: #2bb673; color: #2bb673; }
  .step:disabled { opacity: 0.5; cursor: wait; }

  .actions { margin-top: 8px; display: grid; grid-template-columns: 2fr 1fr; gap: 8px; }
  .photo-btn {
    display: flex; align-items: center; justify-content: center;
    background: var(--ember); color: #fff;
    border-radius: 11px; padding: 11px 8px;
    font-size: 13px; font-weight: 700; cursor: pointer;
    font-family: 'Manrope', sans-serif;
  }
  .photo-btn.busy { opacity: 0.6; }
  .photo-btn input { display: none; }
  .amo {
    background: var(--bg); border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 11px; color: var(--text-mute); font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .amo:active { transform: scale(0.97); }

  .toast {
    position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%);
    background: var(--text); color: var(--bg);
    padding: 10px 18px; border-radius: 12px; font-size: 13px; font-weight: 600;
    z-index: 50; box-shadow: var(--shadow-lg); max-width: 90vw; text-align: center;
  }
</style>
